import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { CalendarDays, ChevronRight } from 'lucide-react'
import { api, type StampContext, type StampUpcomingEventBlock } from '../services/api'
import { useI18n } from '../i18n/useI18n'

// Home 用の開催中告知カード (#1126)。**位置情報プロンプトは出さない** —
// 「どこかの地域でイベントが有効 && 期間内」なら表示し、タップ先の TOP が
// ジオフェンス判定を担う。
//
// #1413: ただし **居ない地域のイベント名を名指ししない**。以前は地域一覧を
// 上から舐めて最初の 1 件を出していたので、平塚に居るのに「修善寺温泉…」と
// 出て、タップ先の TOP (平塚のイベント) と食い違っていた。
//
//   座標があり、地域の圏内 → **その地域だけで判定する** (他の地域は見ない)
//   座標が無く、TOP で地域を選んでいる → **選んだ地域だけで判定する**
//   どちらも無い → 地域一覧。ただし**どの地域に居るか分からないので名前は出さない**
//                 (「近くでイベントが開催されています」とだけ言う)
//
// 以前は圏内でも「現在地に開催が無い」と地域一覧へ落ちていたため、平塚に居るのに
// 修善寺のイベント名が出ていた (上の #1413 の不具合が別の経路で残っていた)。
//
// #1898/#1900 **「スタンプラリー開催中」ではなく「イベント開催中」。** 中には
// スタンプラリーのほかに AR宝探しやミニゲームも並ぶので、入口がスタンプラリーを
// 名乗ると、スタンプを集める気の無い人が素通りする。判定も**企画の種類を問わない**
// — 以前は QR の企画しか見ておらず、AR やミニゲームだけの地域では入口が出なかった。
//
// **開催前のイベントも出す (開催 30 日前から)。** 告知して参加者を増やしたい時期に、
// この入口は開催中しか見ていなかったので HOME に何も出なかった。30 日は管理画面の
// 「HOMEで告知する」(お知らせ枠の掲載開始) と同じ。開催中があればそちらを優先する。
//
// プローブ結果はモジュール変数に持ち、Home の再マウントごとの API 呼び出しを
// 避ける (SPA セッション内 1 回)。座標の有無で結果が変わるのでキーに含める。

/** 開催予定を HOME に出し始める日数。AdminStampEvents の ANNOUNCE_LEAD_DAYS と同じ値 */
const UPCOMING_LEAD_DAYS = 30

interface Probe {
  /** open = 開催中 / upcoming = 30 日以内に始まる / null = 出さない */
  state: 'open' | 'upcoming' | null
  /** 空文字 = 地域を特定できないので名前を出さない */
  eventName: string
  /** upcoming のとき、いちばん早く始まるイベントの開始日時 */
  startsAt: string
}

const NONE: Probe = { state: null, eventName: '', startsAt: '' }

const probeCache = new Map<string, Promise<Probe>>()

/** TOP で自分で選んだ地域 (useStampRally と同じ入れ物)。座標が無いときの「その人の地域」 */
const REGION_KEY = 'yokoso.stampRegion.v1'

function readChosenRegion(): string | null {
  try {
    return sessionStorage.getItem(REGION_KEY)
  } catch {
    return null
  }
}

const startOf = (block: StampUpcomingEventBlock) => new Date(block.event.startsAt).getTime()

/** 30 日以内に始まる予定。API は開催前のものを期限なしで返すので、ここで区切る */
function soonUpcoming(context: StampContext, now: number): StampUpcomingEventBlock[] {
  return context.upcomingEvents.filter(
    (block) => startOf(block) - now <= UPCOMING_LEAD_DAYS * 86_400_000,
  )
}

function upcomingProbe(soon: StampUpcomingEventBlock[], canName: boolean): Probe {
  if (soon.length === 0) return NONE
  const sorted = [...soon].sort((a, b) => startOf(a) - startOf(b))
  return {
    state: 'upcoming',
    // 2 つ以上あるなら 1 つを名指ししない (開催中と同じ)
    eventName: canName && sorted.length === 1 ? sorted[0].event.name : '',
    startsAt: sorted[0].event.startsAt,
  }
}

async function probeEvent(
  coords?: { lat: number; lng: number },
  chosenRegionId?: string | null,
): Promise<Probe> {
  const now = Date.now()
  try {
    if (coords) {
      // TOP と同じ判定。ここで見つかれば遷移先と必ず一致する。
      const context = await api.getStampContext({ lat: coords.lat, lng: coords.lng })
      if (context.region) {
        // 居る地域が分かった。**この地域だけで決める** (他の地域のイベントは出さない)
        if (context.available && context.events.length > 0) {
          // 同じ場所で 2 つ以上のイベントが開催中なら、1 つを名指ししない
          return {
            state: 'open',
            eventName: context.events.length === 1 ? context.events[0].event.name : '',
            startsAt: '',
          }
        }
        return upcomingProbe(soonUpcoming(context, now), true)
      }
    }
    if (chosenRegionId) {
      // TOP で自分で選んだ地域。座標が無くても、その人の地域として扱う
      const context = await api.getStampContext({ regionId: chosenRegionId })
      if (context.available && context.events.length > 0) {
        return {
          state: 'open',
          eventName: context.events.length === 1 ? context.events[0].event.name : '',
          startsAt: '',
        }
      }
      return upcomingProbe(soonUpcoming(context, now), true)
    }
    // 座標も選んだ地域も無い / どの地域の圏内でもない。地域を順に見る。
    // **どの地域に居るか分からないので、イベント名は出さない** — 別の地域の名前を
    // 名指しすると、タップ先の TOP (その人の地域) と食い違う
    const { regions } = await api.getStampRegions()
    const openNames: string[] = []
    const soon: StampUpcomingEventBlock[] = []
    for (const region of regions) {
      const context = await api.getStampContext({ regionId: region.id })
      if (context.available) openNames.push(...context.events.map((block) => block.event.name))
      soon.push(...soonUpcoming(context, now))
      // 名前は出さないので、開催中が 1 件見つかれば十分 (地域は横展開しても数件想定)
      if (openNames.length > 0) break
    }
    if (openNames.length > 0) {
      return { state: 'open', eventName: '', startsAt: '' }
    }
    return upcomingProbe(soon, false)
  } catch {
    // プローブ失敗はカード非表示にするだけ (Home を巻き込まない)
  }
  return NONE
}

export default function StampRallyEntryCard({ lat, lng }: { lat?: number; lng?: number }) {
  const navigate = useNavigate()
  const { t, locale } = useI18n()
  const [event, setEvent] = useState<Probe | null>(null)

  useEffect(() => {
    let cancelled = false
    const coords = lat != null && lng != null ? { lat, lng } : undefined
    // 座標が無いときは、TOP で選んだ地域をその人の地域として使う
    const chosenRegionId = coords ? null : readChosenRegion()
    // 座標は数十m 動いても判定 (地域は半径 km 単位) は変わらないので丸めて
    // キャッシュキーにする — 微小な揺れで再プローブしない。選んだ地域もキーに入れる
    // (地域を選び直した直後に古い結果を出さない)。
    const key = coords
      ? `${coords.lat.toFixed(2)},${coords.lng.toFixed(2)}`
      : `region:${chosenRegionId ?? 'none'}`
    let promise = probeCache.get(key)
    if (!promise) {
      promise = probeEvent(coords, chosenRegionId)
      probeCache.set(key, promise)
    }
    promise.then((result) => {
      if (!cancelled) setEvent(result)
    })
    return () => {
      cancelled = true
    }
  }, [lat, lng])

  if (!event?.state) return null

  const upcoming = event.state === 'upcoming'
  const date = upcoming
    ? new Date(event.startsAt).toLocaleDateString(locale, { month: 'long', day: 'numeric' })
    : ''
  const subtitle = upcoming
    ? event.eventName
      ? t('stampRally.entryUpcomingEvent', { date, name: event.eventName })
      : t('stampRally.entryUpcomingNearby', { date })
    : event.eventName || t('stampRally.entryNearby')

  return (
    <button
      // 開催予定しか無い時期は、TOP も「開催予定」タブから開く
      onClick={() => navigate('/stamp-rally')}
      className="tap-card animate-fade-in mx-4 mt-4 flex w-[calc(100%-2rem)] items-center gap-3 rounded-2xl bg-gradient-to-r from-matcha/15 to-gold/15 p-4 text-left shadow-card"
    >
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-matcha text-white">
        <CalendarDays size={20} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-display text-sm font-semibold text-text-primary">
          {t(upcoming ? 'stampRally.entryUpcomingTitle' : 'stampRally.entryTitle')}
        </p>
        <p className="mt-0.5 truncate font-body text-xs text-text-secondary">{subtitle}</p>
      </div>
      <span className="flex shrink-0 items-center gap-0.5 font-body text-[13px] font-medium text-yokoso-red">
        {t(upcoming ? 'stampRally.entryUpcomingCta' : 'stampRally.entryCta')}
        <ChevronRight size={16} />
      </span>
    </button>
  )
}
